import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminCredits,
  useCheckCredits,
  useRefreshCredits,
} from "@/features/admin";
import { toast } from "sonner";
import { AdminPageLayout } from "../layout";
import { ProviderCreditCard } from "./components/credit-card";
import { when } from "./components/credit-format";
import { StatCard } from "./components/stat-card";

/**
 * Credits panel — "which of the accounts we run on is about to run dry?".
 *
 * The five vendors expose very different things (Twilio a real balance, ElevenLabs a
 * character quota, OpenAI/Anthropic only spend, Mistral nothing at all), so the panel shows
 * the *source* next to every figure. A number whose origin is a two-week-old manual entry
 * should not read like one pulled from Twilio a minute ago.
 *
 * Alerts themselves go to Sentry, from the backend cron. This page is the standing view; it
 * never alerts on its own.
 */
export function AdminCreditsPage() {
  const { data: credits = [], isLoading } = useAdminCredits();
  const refresh = useRefreshCredits();
  const check = useCheckCredits();

  const low = credits.filter((c) => c.status === "low");
  const unknown = credits.filter(
    (c) => c.status === "unknown" || c.status === "error",
  );

  const handleRefresh = async () => {
    try {
      await refresh.mutateAsync();
      toast.success("Soldes rafraîchis");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Rafraîchissement impossible",
      );
    }
  };

  const handleCheck = async () => {
    try {
      const result = await check.mutateAsync();
      const alerted = result.filter((c) => c.status === "low").length;
      toast.success(
        alerted
          ? `${alerted} fournisseur(s) sous le seuil — alerte envoyée sur Sentry`
          : "Aucun fournisseur sous le seuil",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Vérification impossible",
      );
    }
  };

  return (
    <AdminPageLayout
      title="Crédits fournisseurs"
      description="Solde des comptes sur lesquels tourne la plateforme — Mistral, Twilio, Anthropic, OpenAI, ElevenLabs"
      actions={[
        <div className="flex items-center gap-2" key="actions">
          <Button
            variant="outline"
            onClick={handleCheck}
            disabled={check.isPending}
          >
            {check.isPending ? "Vérification…" : "Vérifier maintenant"}
          </Button>
          <Button onClick={handleRefresh} disabled={refresh.isPending}>
            {refresh.isPending ? "Rafraîchissement…" : "Rafraîchir"}
          </Button>
        </div>,
      ]}
    >

      <p className="text-xs text-muted-foreground">
        Les soldes sont relevés automatiquement toutes les 30 minutes, et une
        alerte part sur Sentry dès qu'un compte passe sous son seuil (2 $ par
        défaut) — une fois par fournisseur et par fenêtre de silence, pas à
        chaque passage. Un solde « inconnu » n'alerte jamais : c'est un manque
        d'information, pas un compte vide. « Vérifier maintenant » rejoue la
        passe complète, alerte comprise.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Comptes sous le seuil"
          value={low.length}
          hint={low.length ? low.map((c) => c.label).join(", ") : "aucun"}
          tone={low.length ? "bad" : "default"}
          isLoading={isLoading}
        />
        <StatCard
          title="Soldes connus"
          value={`${credits.filter((c) => c.balance !== null).length} / ${credits.length}`}
          isLoading={isLoading}
        />
        <StatCard
          title="Sans information"
          value={unknown.length}
          hint={
            unknown.length ? unknown.map((c) => c.label).join(", ") : "aucun"
          }
          tone={unknown.length ? "warn" : "default"}
          isLoading={isLoading}
        />
        <StatCard
          title="Dernier relevé"
          value={when(Math.max(0, ...credits.map((c) => c.checked_at)))}
          isLoading={isLoading}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {credits.map((credit) => (
            <ProviderCreditCard key={credit.provider} credit={credit} />
          ))}
        </div>
      )}
    </AdminPageLayout>
  );
}
